/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { ReactNode } from 'react';
import Text from '@/shared/ui/components/basic/Text';
import UnstyledButton from '@/shared/ui/components/basic/UnstyledButton';
import {
  blockActionStyle, blockCaptionStyle, blockCardStyle, blockHeaderStyle,
  blockHeaderTextStyle, blockNoteStyle, blockTitleStyle,
} from './settings-block.styles';

/** The block header's single optional action. */
export interface SettingsBlockAction {
  /** Already-localized button label. */
  label: string;
  /** What the action does. */
  onClick: () => void;
  /** Disables the button while a save or fetch is in flight. */
  disabled?: boolean;
  /** Test id for the action button. */
  testId?: string;
}

export interface SettingsBlockProps {
  /**
   * Already-localized block title. Omit it when the page already draws this
   * section's heading once above every block (e.g. via a shared
   * per-tab heading component) and this block's own title/caption would just
   * repeat that same text -- the header row is skipped entirely rather than
   * rendered empty.
   */
  title?: string;
  /**
   * Already-localized explanation. This is where the prose that used to
   * float above the cards lives, so every block should carry one.
   */
  caption?: string;
  /**
   * At most one action, drawn as an outline button on the right. The cap is
   * the point: a header with two competing buttons is a header that has
   * stopped saying which one matters.
   */
  action?: SettingsBlockAction;
  /** The caveat line under the mid, when the mid needs qualifying. */
  note?: string;
  /** Test id for the block card. */
  testId?: string;
  /** One of the six mids. */
  children?: ReactNode;
}

/**
 * The one card every settings section draws.
 *
 * Header — title, explanation, at most one action — and below it one of the
 * six mids (`fields`, `people`, `toggles`, `matrix`, `plan`/`upgrade`,
 * `danger`). Sections compose these; they do not draw their own cards.
 *
 * @param props - Component props.
 * @returns The block element.
 */
export function SettingsBlock({
  title, caption, action, note, testId, children,
}: SettingsBlockProps) {
  return (
    <section style={blockCardStyle} data-testid={testId}>
      {/* A block without a title still keeps its caption and its action: a
          host that already drew the heading dropped the words, not the
          controls. */}
      {(title || caption || action) && (
        <div style={blockHeaderStyle}>
          <div style={blockHeaderTextStyle}>
            {title && <Text component="span" style={blockTitleStyle}>{title}</Text>}
            {caption && <Text component="span" style={blockCaptionStyle}>{caption}</Text>}
          </div>
          {action && (
            <UnstyledButton
              type="button"
              onClick={action.onClick}
              disabled={action.disabled}
              data-testid={action.testId}
              style={{ ...blockActionStyle, opacity: action.disabled ? 0.6 : 1 }}
            >
              {action.label}
            </UnstyledButton>
          )}
        </div>
      )}
      {children}
      {note && <Text component="span" style={{ ...blockNoteStyle, display: 'block' }}>{note}</Text>}
    </section>
  );
}
